import { useEffect, useState } from "react";
import Toolbar from "../../../components/toolbar";
import _ from "lodash";
import Reveal from "../../../components/animation";
import Button from "../../../components/button";
import styles from "./edit.module.scss";
import Slider from "../../../components/slider";
import AssetIcon from "src/components/asset/icon";
import GroupIcon from "src/components/groupIcon/index";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { deleteAssetFromGroup, deleteGroup } from "../../../redux/actions/groups";
import { copyEntities, moveEntities } from "../../../redux/actions/entities";
import { Asset } from "src/types/assets";
import { Group } from "src/types/groups";
import { SelectStreamEntity } from "src/components/selectStreamEntity";
import Truncate from "src/components/truncate";
import Tooltip from "src/components/tooltip";
import { useTransition, animated } from "@react-spring/web";
import cx from "classnames";
import { GROUP } from "src/constants/adoption/sourceTypes";
import { useStreamsAdoption } from "src/redux/actions/adoption/streams";
import { IUseFetchProps, useFetch } from "src/components/hooks/useFetch";

const isAsset = (entity: Asset | Group): entity is Asset => {
  return entity.entityType === 'asset';
};

const isGroup = (entity: Asset | Group): entity is Group => {
  return entity.entityType === 'group';
};
interface RenameMenubarProps {
  show: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const RenameMenubar: React.FC<RenameMenubarProps> = ({
  show, onConfirm, onCancel
}) => {

  return <Reveal show={show}
    className={styles.renameConfirmation}
  >
    <div
      className={styles.message}
    >
      All changes made to this section will reflect for all users in the content hub.
    </div>

    <Button variant='primary'
      text={'Save'}
      icon="paperflite-tick"
      onClick={onConfirm}
    />

    <Button variant='secondary'
      text="Cancel"
      onClick={onCancel} />
  </Reveal>
};

interface DeleteConfirmationDialogProps {
  show: boolean;
  entities: (Asset | Group)[];
  onFinish: () => void;
  onClose: () => void;
  streamId: string;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  show, entities, onFinish, onClose, streamId
}) => {

  const dispatch = useDispatch();
  const { trackDeleteConfirmClicked, trackAssetDeleted } = useStreamsAdoption();

  const { id } = useParams<{ id: string }>();

  const [showDataSourcesWarning, setShowDataSourcesWarning] = useState(false);
  const [dataSourceEntities, setDataSourceEntities] = useState<(Asset | Group)[]>([]);

  const deleting = useSelector((state: any) => {
    if (!id) return false;
    const groupState = state.group[id];
    return groupState?.assetsDeletion?.deleting || groupState?.groupsDeletion?.deleting || false;
  });

  const deleted = useSelector((state: any) => {
    if (!id) return false;
    const groupState = state.group[id];
    return groupState?.assetsDeletion?.deleted || groupState?.groupsDeletion?.deleted || false;
  });

  useEffect(() => {
    if (deleted) {
      if (onClose) {
        onClose();
      }
    }
  }, [deleted])

  useEffect(() => {
    isDataSourceAssets();
  }, [entities]);

  const handleConfirm = () => {
    if (_.size(entities) == 0) {
      return;
    }

    var assets: string[] = [];

    entities.forEach((entity) => {
      if (entity.entityType === "asset") {
        assets.push(entity.id);
      } else if (entity.entityType === "group") {
        dispatch(deleteGroup({ id: entity.id, groupId: id || "", streamId: "" }));
      }
    })

    if(assets.length > 0){
      dispatch(deleteAssetFromGroup({ groupId: id || "", assets: assets, event: trackAssetDeleted, sourceCta: GROUP }));
    }

    const groupIds = entities.filter(isGroup).map(group => group.id);
    const assetIds = entities.filter(isAsset).map(asset => asset.id);
    trackDeleteConfirmClicked({streamId: streamId, groupIds: groupIds, assetIds: assetIds, sourceGroupId: id});
  }

  const isDataSourceAssets = () => {
    const dataSourceEntities = entities.filter((entity) => !entity.canDelete);
    setDataSourceEntities(dataSourceEntities);

    if (entities.some((entity) => !entity.canDelete)) {
      setShowDataSourcesWarning(true);
    } else {
      setShowDataSourcesWarning(false);
    }
  }

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  }

  const renderEntities = () => {
    return entities.map((entity) => {
      if (isAsset(entity)) {
        return (
          <div className={styles.entity}>
            <AssetIcon key={entity.id} asset={entity} />
            <Tooltip content={entity.name} placement='top'>
              <span className={styles.entityDetails}>
                {!entity.canDelete && <i className="paperflite-danger"></i>}
                <span className={styles.name}>{entity.name}</span>
              </span>
            </Tooltip>
          </div>
        )
      } else if (isGroup(entity)) {
        return (
          <div className={styles.entity}>
            <GroupIcon key={entity.id} group={entity} />
            <Tooltip content={entity.name} placement='top'>
              <span className={styles.entityDetails}>
                {!entity.canDelete && <i className="paperflite-danger"></i>}
                <span className={styles.name}>{entity.name}</span>
              </span>
            </Tooltip>
          </div>
        )
      }
      return null;
    });
  }

  const assetCount = (entities as Array<Asset | Group>).filter(isAsset).length;
  const groupCount = (entities as Array<Asset | Group>).filter(isGroup).length;

  const assetMessage = assetCount > 0 ? `${assetCount} Asset${assetCount > 1 ? 's' : ''}` : '';
  const groupMessage = groupCount > 0 ? `${groupCount} Group${groupCount > 1 ? 's' : ''}` : '';

  const message = [assetMessage, groupMessage].filter(Boolean).join(', ');

  return (
    <Slider.Dialog className={styles.deleteConfirmDialog} width="300" show={show} onClose={onClose}>
      <Slider.Header
        title="deleting..."
        message={message}
      >
        <p className={styles.description}>Are you really, really sure you want to delete the selected groups and assets? No takebacksies</p>
      </Slider.Header>
      <Slider.Body>
        <div className={styles.entities}>
          {renderEntities()}
        </div>

        {showDataSourcesWarning &&
          <div className={styles.warningMessage}>
            <i className={cx(styles.closeIcon, "paperflite-close")} onClick={() => setShowDataSourcesWarning(false)}></i>
            <span className={styles.warningHeader}>
              <i className="paperflite-danger" onClick={() => setShowDataSourcesWarning(false)}></i>
              <span>Synced with datasource</span>
            </span>

            <span className={styles.warningDescription}>
              Some assets can’t be deleted; they’re still attached to the content source. Breakups are tough!
            </span>
          </div>
        }
      </Slider.Body>
      <Slider.Actions>
        <Button icon="paperflite-tick"
          text={deleting ? 'Deleting' : 'Confirm'}
          variant='primary'
          disabled={deleting || (_.size(entities) === _.size(dataSourceEntities))}
          onClick={handleConfirm}
        />
        <Button text="Cancel"
          variant='secondary'
          disabled={deleting}
          onClick={handleCancel}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
}



interface EditGroupToolbarProps {
  show: boolean;
  onFinish: () => void;
  entities: (Asset | Group)[];
  streamId: string;
  groupId: string;
}

export const EditGroupToolbar: React.FC<EditGroupToolbarProps> = ({
  show, onFinish, entities, streamId, groupId
}) => {

  const { trackDeleteClicked, trackCopyClicked, trackMoveClicked } = useStreamsAdoption();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copyConfirm, setCopyConfirm] = useState(false);
  const [moveConfirm, setMoveConfirm] = useState(false);

  const handleCopyConfirm = () => {
    trackCopyClicked({sourceStreamId: streamId, sourceCta: GROUP});
    setCopyConfirm(true);
  }

  const handleMoveConfirm = () => {
    trackMoveClicked({sourceStreamId: streamId, sourceCta: GROUP});
    setMoveConfirm(true);
  }

  const handleDeleteConfirm = () => {
    setConfirmDelete(true);
    trackDeleteClicked({streamId: streamId, sourceCta: GROUP});
  }

  const handleConfirm = () => {

    setCopyConfirm(false);
    setMoveConfirm(false);
    setConfirmDelete(false);
    onFinish();
  }

  return (
    <>
      <Toolbar.Container show={show}>
        <Toolbar.Text>
          <span>{_.size(entities)} Selected</span>
        </Toolbar.Text>
        <Toolbar.Divider />
        <Toolbar.IconButton icon='paperflite-copy' tooltip='Copy' disabled={_.size(entities) === 0} onClick={handleCopyConfirm} />
        <Toolbar.IconButton icon='paperflite-move' tooltip='Move' disabled={_.size(entities) === 0} onClick={handleMoveConfirm} />
        <Toolbar.IconButton icon='paperflite-delete' tooltip='Delete' disabled={_.size(entities) === 0} onClick={handleDeleteConfirm} />
      </Toolbar.Container>
      <DeleteConfirmationDialog
        show={confirmDelete}
        entities={entities}
        onFinish={handleConfirm}
        onClose={() => {
          setConfirmDelete(false)
          onFinish()
        }}
        streamId = {streamId}
      />
      <ActionConfirmationDialog
        show={moveConfirm || copyConfirm}
        action={moveConfirm ? "move" : "copy"}
        title={moveConfirm ? "moving..." : "copy..."}
        entities={entities}
        onFinish={handleConfirm}
        onClose={() => {
          setMoveConfirm(false)
          setCopyConfirm(false)
        }}
        streamId={streamId}
      />
    </>
  );
};

interface EditGroupMenuBarProps {
  show: boolean;
  onCancel: () => void;
}

export const EditGroupMenuBar: React.FC<EditGroupMenuBarProps> = ({
  show, onCancel
}) => {
  return <Reveal show={show}
    className={styles.editConfirmation}
  >
    <div
      className={styles.message}
    >
      All changes made to this section will reflect for all users in the content hub.
    </div>

    <Button variant='secondary'
      text="Cancel"
      onClick={onCancel} />
  </Reveal>
}

interface ActionConfirmationDialog {
  show: boolean;
  title: string;
  action: string;
  entities: (Asset | Group)[];
  onFinish: () => void;
  onClose: () => void;
  streamId: string;
}

export const ActionConfirmationDialog: React.FC<ActionConfirmationDialog> = ({
  show, title, action, entities, onFinish, onClose, streamId
}) => {

  const dispatch = useDispatch();

  const { id } = useParams<{ id: string }>();
  const { trackCopyStreamSelected, trackCopyConfirmClicked, trackMoveConfirmClicked, trackMoveStreamSelected, trackAssetsCopied, trackGroupsCopied, trackAssetsMoved, trackGroupsMoved } = useStreamsAdoption();

  const group = useSelector((state: any) => _.get(state, `group[${id}].item`, null));
  const copying = useSelector((state: any) => _.get(state, `entities.copy.copying`, false));
  const moving = useSelector((state: any) => _.get(state, `entities.move.moving`, false));
  const copied = useSelector((state: any) => _.get(state, `entities.copy.copied`, false));
  const moved = useSelector((state: any) => _.get(state, `entities.move.moved`, false));
  const stream = useSelector((state: any) => _.get(state, `stream.items[${streamId}].item`, null));

  const [destinationEntity, setDestinationEntity] = useState<any>(null);
  const [destinationEntities, setDestinationEntities] = useState<any[]>([]);
  const [showDataSourcesWarning, setShowDataSourcesWarning] = useState(false);
  const [showEntities, setShowEntities] = useState(false);
  const [displayEntities, setDisplayEntities] = useState(false);
  const [dataSourceEntities, setDataSourceEntities] = useState<(Asset | Group)[]>([]);

  const copyEntitiesProps: IUseFetchProps = {
    action: copyEntities,
    loading: copying,
    succeeded: copied,
    response: [],
    error: null
  };

  const { handleFetch: copyEntitiesRequest } = useFetch(copyEntitiesProps);

  const moveEntitiesProps: IUseFetchProps = {
    action: moveEntities,
    loading: moving,
    succeeded: moved,
    response: [],
    error: null
  };

  const { handleFetch: moveEntitiesRequest } = useFetch(moveEntitiesProps);

  useEffect(() => {
    isDataSourceAssets();
  }, [entities, action]);

  useEffect(() => {
    if (onFinish && (copied || moved)) {
      setDestinationEntity(null);
      setDestinationEntities([]);
      onFinish();
    }
  }, [copied, moved])

  const handleConfirm = () => {
    if (_.size(entities) == 0) {
      return;
    }

    const requestEntities: { id: string; entityType: string; }[] = [];

    entities.forEach((entity) => {
      if (entity.entityType === "group") {
        requestEntities.push({
          id: entity.id,
          entityType: "group"
        })
      } else {
        requestEntities.push({
          id: entity.id,
          entityType: "asset"
        })
      }
    })

    const request = {
      source: group,
      destination: action === 'copy' ? destinationEntities : destinationEntity,
      entities: requestEntities
    }

    const groupIds = entities.filter(isGroup).map(group => group.id);
    const assetIds = entities.filter(isAsset).map(asset => asset.id);

    if (action === "copy") {
      trackCopyConfirmClicked({streamId: streamId, assetIds: assetIds, groupIds: groupIds, sourceGroupId: id});
      copyEntitiesRequest({ request }).then(() => {
      const assetsCopied = entities.filter(isAsset);
      if(_.size(assetsCopied) > 0){
      trackAssetsCopied({sourceStream: stream, sourceGroup: group || '', entities: assetsCopied, destination: destinationEntity});
      }

      const groupsCopied = entities.filter(isGroup);
      if(_.size(groupsCopied) > 0){
        trackGroupsCopied({sourceStream: stream, sourceGroup: group || '', entities: groupsCopied, destination: destinationEntity});
      }
      });

    } else {
      trackMoveConfirmClicked({streamId: streamId, assetIds: assetIds, groupIds: groupIds, sourceGroupId: id});
      moveEntitiesRequest({ request }).then(() => {
        const assetsMoved = entities.filter(isAsset);
        if(_.size(assetsMoved) > 0){
          trackAssetsMoved({sourceStream: stream, sourceGroup: group || '', entities: assetsMoved, destination: destinationEntity});
        }
        const groupsMoved = entities.filter(isGroup);
        if(_.size(groupsMoved) > 0){
          trackGroupsMoved({sourceStream: stream, sourceGroup: group || '', entities: groupsMoved, destination: destinationEntity});
        }
      });
    }
  }

  const handleCancel = () => {
    if (onClose) {
      setDestinationEntity(null);
      setDestinationEntities([]);
      onClose();
    }
  }

  const isDataSourceAssets = () => {
    const dataSourceEntities = entities.filter((entity) => !entity.canDelete);
    setDataSourceEntities(dataSourceEntities);

    if ((entities.some(entity => !entity.canDelete)) && action === "move" && !showDataSourcesWarning) {
      setShowDataSourcesWarning(true);
    } else {
      setShowDataSourcesWarning(false);
    }
  }

  const renderEntities = () => {
    return entities.map((entity) => {
      if (isAsset(entity)) {
        return (
          <div className={styles.entity}>
            <AssetIcon key={entity.id} asset={entity} />
            <Tooltip content={entity.name} placement='top'>
              <span className={styles.entityDetails}>
                <i className="paperflite-danger"></i>
                <span className={styles.name}>{entity.name}</span>
              </span>
            </Tooltip>
          </div>
        )
      } else if (isGroup(entity)) {
        return (
          <div className={styles.entity}>
            <GroupIcon key={entity.id} group={entity} />
            <Tooltip content={entity.name} placement='top'>
              <span className={styles.entityDetails}>
                <i className="paperflite-danger"></i>
                <span className={styles.name}>{entity.name}</span>
              </span>
            </Tooltip>
          </div>
        )
      }
      return null;
    });
  }

  const transitions = useTransition(!showEntities && !displayEntities, {
    from: { transform: "translateX(-100%)", opacity: 1 },
    enter: { transform: "translateX(0%)", opacity: 1 },
    leave: { transform: "translateX(-100%)", opacity: 0 },
    onRest: () => {
      if (showEntities) {
        setDisplayEntities(true); 
      }
    },
  });

  const entitiesTransitions = useTransition(showEntities && displayEntities, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    onRest: () => {
      if (!showEntities) {
        setDisplayEntities(false); 
      }
    },
  });

  const assetCount = (entities as Array<Asset | Group>).filter(isAsset).length;
  const groupCount = (entities as Array<Asset | Group>).filter(isGroup).length;

  const assetMessage = assetCount > 0 ? `${assetCount} Asset${assetCount > 1 ? 's' : ''}` : '';
  const groupMessage = groupCount > 0 ? `${groupCount} Group${groupCount > 1 ? 's' : ''}` : '';

  const message = [assetMessage, groupMessage].filter(Boolean).join(', ');

  const handleSelection = (entity: any) => {

     if(action === "copy"){
      if(destinationEntities.some((e) => e.id === entity.id)){
        setDestinationEntities(destinationEntities.filter((e) => e.id !== entity.id));
      } else {
        setDestinationEntities([...destinationEntities, entity]);
      }
    } else {

      setDestinationEntity(entity);
    }
    const groupIds = entities.filter(isGroup).map(group => group.id);
    const assetIds = entities.filter(isAsset).map(asset => asset.id);
    if(action === "copy"){
      trackCopyStreamSelected({streamId: streamId || '', groupIds, assetIds, destinationStreamName: entity.name, sourceGroupId: id});
    } else {
      trackMoveStreamSelected({streamId: streamId || '', groupIds, assetIds, destinationStreamName: entity.name, sourceGroupId: id});
    }
  }

  return (
    <Slider.Dialog className={styles.actionConfirmDialog} width="300" show={show} onClose={onClose}>
      <Slider.Header
        icon={showEntities ? "paperflite-left-arrow" : ""}
        title={title}
        message={message}
        handleClick={() => {
          setShowEntities(false)
        }}
      >
        <p className={styles.description}>
          {showEntities ? `The following entities cannot be moved` : 
          `Choose a stream or group to ${action}. Like taking the best bits of lunch - sneaky!`}
        </p>
      </Slider.Header>
      <Slider.Body>

        {transitions((style, item) =>
          item ? (
            <animated.div style={style}>
              <SelectStreamEntity style={{}} width={340} onSelection={(entity) => handleSelection(entity)} moveTo={destinationEntity} copyTo={destinationEntities}/>

              {showDataSourcesWarning &&
                <div className={cx(styles.warningMessage, {
                  [styles.moveWarningMessage]: destinationEntity
                })}>
                  <i className={cx(styles.closeIcon, "paperflite-close")} onClick={() => setShowDataSourcesWarning(false)}></i>
                  <span className={styles.warningHeader}>
                    <i className="paperflite-danger"></i>
                    <span>Synced with datasource</span>
                  </span>

                  <span className={styles.warningDescription}>
                    <span className={styles.entitiesCount} onClick={() => setShowEntities(true)}>{_.size(dataSourceEntities)} groups & assets </span> cannot be moved as it is synced with datasource.
                  </span>
                </div>
              }
            </animated.div>
          ) : null
        )}

        {entitiesTransitions((style, item) =>
          item ? (
            <animated.div style={style} className={styles.entities}>
              {renderEntities()}
            </animated.div>
          ) : null
        )}

      </Slider.Body>

      {transitions((style, item) =>
        item ? (
          <animated.div style={style}>
            <Slider.Actions>

              {destinationEntity && (
                <p className={styles.actionContent}>
                  <Truncate
                    lines={1}
                  >
                    Map into files
                    <Tooltip placement="top" content={destinationEntity.name}>
                      <span className={styles.destinationName}>
                        "{destinationEntity.name}"
                      </span>
                    </Tooltip>

                  </Truncate>
                </p>
              )}

              <Button icon="paperflite-tick"
                text={copying ? 'Copying...' : (moving ? 'Moving...' : 'Confirm')}
                variant='primary'
                disabled={(!destinationEntity && _.size(destinationEntities) === 0) || copying || moving || action === "move" && (_.size(entities) === _.size(dataSourceEntities))}
                onClick={handleConfirm}
              />
              <Button text="Cancel"
                variant='secondary'
                disabled={copying || moving}
                onClick={handleCancel}
              />
            </Slider.Actions>
          </animated.div>
        ) : null
      )}
    </Slider.Dialog>
  );
}